import { createContext, useContext, useReducer, useMemo, useEffect } from "react";

// prop-types is a library for typechecking of props
import PropTypes from "prop-types";

// Material Dashboard 2 React main context
const MaterialUI = createContext();

// Setting custom name for the context which is visible on react dev tools
MaterialUI.displayName = "MaterialUIContext";

const THEME_CACHE_NAME = 'p360-theme-config';

const getStoredThemePreference = () => {
  if (typeof window === "undefined") {
    return null;
  }

  const cachedTheme = window.localStorage.getItem(THEME_CACHE_NAME);

  if (cachedTheme === null) {
    return null;
  }

  try {
    const parsedTheme = JSON.parse(cachedTheme);
    return typeof parsedTheme === "boolean" ? parsedTheme : null;
  } catch (error) {
    return null;
  }
};

const getSystemDarkModePreference = () => {
  if (typeof window === "undefined" || typeof window.matchMedia !== "function") {
    return false;
  }

  return window.matchMedia("(prefers-color-scheme: dark)").matches;
};

const resolveInitialDarkMode = () => {
  const storedPreference = getStoredThemePreference();

  if (typeof storedPreference === "boolean") {
    return storedPreference;
  }

  return getSystemDarkModePreference();
};

// Material Dashboard 2 React reducer
function reducer(state, action) {
  switch (action.type) {
    case "MINI_SIDENAV": {
      if (state.miniSidenav === action.value) return state;
      return { ...state, miniSidenav: action.value };
    }
    case "TRANSPARENT_SIDENAV": {
      if (state.transparentSidenav === action.value) return state;
      return { ...state, transparentSidenav: action.value };
    }
    case "WHITE_SIDENAV": {
      if (state.whiteSidenav === action.value) return state;
      return { ...state, whiteSidenav: action.value };
    }
    case "SIDENAV_COLOR": {
      if (state.sidenavColor === action.value) return state;
      return { ...state, sidenavColor: action.value };
    }
    case "TRANSPARENT_NAVBAR": {
      if (state.transparentNavbar === action.value) return state;
      return { ...state, transparentNavbar: action.value };
    }
    case "FIXED_NAVBAR": {
      if (state.fixedNavbar === action.value) return state;
      return { ...state, fixedNavbar: action.value };
    }
    case "OPEN_CONFIGURATOR": {
      if (state.openConfigurator === action.value) return state;
      return { ...state, openConfigurator: action.value };
    }
    case "DIRECTION": {
      if (state.direction === action.value) return state;
      return { ...state, direction: action.value };
    }
    case "LAYOUT": {
      if (state.layout === action.value) return state;
      return { ...state, layout: action.value };
    }
    case "DARKMODE": {
      if (state.darkMode === action.value) return state;

      if (action.persist !== false && typeof window !== "undefined") {
        window.localStorage.setItem(THEME_CACHE_NAME, JSON.stringify(action.value));
      }

      return { ...state, darkMode: action.value };
    }
    case "USER_TIER":{
      if (state.userTier === action.value) return state;
      return { ...state, userTier: action.value };
    }

    default: {
      throw new Error(`Unhandled action type: ${action.type}`);
    }
  }
}

// Material Dashboard 2 React context provider
function MaterialUIControllerProvider({ children }) {
  const initialState = {
    miniSidenav: true,
    transparentSidenav: false,
    whiteSidenav: false,
    sidenavColor: "info",
    transparentNavbar: true,
    fixedNavbar: true,
    openConfigurator: false,
    direction: "ltr",
    layout: "dashboard",
    darkMode: resolveInitialDarkMode(),
    userTier: 1
  };

  const [controller, dispatch] = useReducer(reducer, initialState);

  useEffect(() => {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") {
      return undefined;
    }

    const mediaQuery = window.matchMedia("(prefers-color-scheme: dark)");

    const handleSystemThemeChange = (event) => {
      if (getStoredThemePreference() !== null) {
        return;
      }

      dispatch({ type: "DARKMODE", value: event.matches, persist: false });
    };

    if (typeof mediaQuery.addEventListener === "function") {
      mediaQuery.addEventListener("change", handleSystemThemeChange);

      return () => mediaQuery.removeEventListener("change", handleSystemThemeChange);
    }

    mediaQuery.addListener(handleSystemThemeChange);

    return () => mediaQuery.removeListener(handleSystemThemeChange);
  }, []);

  const value = useMemo(() => [controller, dispatch], [controller, dispatch]);

  return <MaterialUI.Provider value={value}>{children}</MaterialUI.Provider>;
}

// Material Dashboard 2 React custom hook for using context
function useMaterialUIController() {
  const context = useContext(MaterialUI);

  if (!context) {
    throw new Error(
      "useMaterialUIController should be used inside the MaterialUIControllerProvider."
    );
  }

  return context;
}

// Context module functions
const setMiniSidenav = (dispatch, value) => dispatch({ type: "MINI_SIDENAV", value });
const setTransparentSidenav = (dispatch, value) => dispatch({ type: "TRANSPARENT_SIDENAV", value });
const setWhiteSidenav = (dispatch, value) => dispatch({ type: "WHITE_SIDENAV", value });
const setSidenavColor = (dispatch, value) => dispatch({ type: "SIDENAV_COLOR", value });
const setTransparentNavbar = (dispatch, value) => dispatch({ type: "TRANSPARENT_NAVBAR", value });
const setFixedNavbar = (dispatch, value) => dispatch({ type: "FIXED_NAVBAR", value });
const setOpenConfigurator = (dispatch, value) => dispatch({ type: "OPEN_CONFIGURATOR", value });
const setDirection = (dispatch, value) => dispatch({ type: "DIRECTION", value });
const setLayout = (dispatch, value) => dispatch({ type: "LAYOUT", value });
const setDarkMode = (dispatch, value) => dispatch({ type: "DARKMODE", value });
const setUserTier = (dispatch, value) => dispatch({ type: "USER_TIER", value });


export {
  MaterialUIControllerProvider,
  useMaterialUIController,
  setMiniSidenav,
  setTransparentSidenav,
  setWhiteSidenav,
  setSidenavColor,
  setTransparentNavbar,
  setFixedNavbar,
  setOpenConfigurator,
  setDirection,
  setLayout,
  setDarkMode,
  setUserTier, 
};



// Typechecking props for the MaterialUIControllerProvider
MaterialUIControllerProvider.propTypes = {
  children: PropTypes.node.isRequired,
};
